// src/components/.../JourneyPost.tsx
"use client";

import React, { useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  MessageCircleReply,
  HeartPlus,
  MessageSquareShare,
  SquareArrowOutUpRight,
  Waypoints,
} from "lucide-react";

import { selectAuthUser } from "../../../../../../redux/selectors/profile/userSelectors";
import ShareToFriendModalRedux from "../../../../../share/ShareToFriendModalRedux";
import { toDate, formatYMonD } from "../../../../../../utils/helpers/chatDate";
import { logger } from "../../../../../../utils/logging/logger";
import "./JourneyPost.css";

// Shared share helpers (canonical + text + fallbacks)
import {
  pageCanonicalHref,
  buildShareText,
  shareWithFallback,
} from "../../../../../../utils/share/shareUtils";

const AVAILABILITY_ISO = "2026-09-01";

export default function JourneyPost() {
  const me = useSelector(selectAuthUser) as { id?: number } | null;

  // Format availability date for badge
  const availabilityLabel = useMemo(() => {
    const tz =
      (typeof Intl !== "undefined" &&
        Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      "UTC";
    return formatYMonD(toDate(AVAILABILITY_ISO), tz);
  }, []);

  // Build canonical deep-link to this section (#journeys)
  const buildShareUrl = useCallback((): string => {
    const base = pageCanonicalHref(); // normalize current URL to production domain
    if (!base) return "";
    const u = new URL(base);
    u.hash = "journeys";
    return u.toString();
  }, []);

  // System share with unified fallbacks (native → clipboard → mailto)
  const handleExternalShare = useCallback(async () => {
    try {
      const url = buildShareUrl();
      const shareTitle = "Journeys — A daily path, thoughtfully shared.";
      const shareText = buildShareText(
        shareTitle,
        /* ownerHandle */ undefined,
        url // include URL to increase chance of clickable link
      );

      await shareWithFallback({ title: shareTitle, text: shareText, url });
      // Optionally: show a toast on clipboard fallback
      // toast.success("Link copied to clipboard");
    } catch (e: any) {
      logger.error("External share failed (journeys)", { error: e?.message });
      alert("Share failed. Please try again.");
    }
  }, [buildShareUrl]);

  // Share-to-friend modal state
  const [shareOpen, setShareOpen] = useState(false);

  return (
    <section className="journey-wrap" id="journeys" aria-label="Journeys">
      {/* Header */}
      <header className="journey-header">
        <div className="journey-titleblock">
          <div className="journey-icon" aria-hidden="true">
            <Waypoints size={38} />
          </div>
        </div>

        <div className="journey-headings">
          <h3 className="journey-title">Journeys</h3>
          <p className="journey-tagline">A daily path, thoughtfully shared.</p>
        </div>

        {/* Availability Badge */}
        <span
          className="journey-badge"
          title={`Available by ${availabilityLabel}`}
        >
          Available by {availabilityLabel}
        </span>
      </header>

      {/* Body copy */}
      <div className="journey-body">
        <p className="journey-blurb-short">
          Post a day-by-day story. Before sharing, take a mindful minute to reflect—your insights stay private, while your Journey inspires others.
        </p>
        <p className="journey-blurb-long">
          Journeys is a gentle, story-like stream of daily posts. A brief pre-share reflection helps you check in with yourself; insights remain private to you.
          Over time, you’ll receive personal progress summaries (delivered only to you) to encourage the next step.
        </p>

        {/* Feature hints */}
        <ul className="journey-features">
          <li>Story-like daily posts with lightweight sharing</li>
          <li>Private pre-share reflection (your insights are yours)</li>
          <li>Periodic personal progress summaries sent to you</li>
        </ul>
      </div>

      {/* Actions (unified with other viewers) */}
      <div className="journey-actions">
        <span
          className="journey-action journey-action--muted"
          title="Reactions — Coming Soon. Thanks for your patience!"
          aria-label="Reactions coming soon"
        >
          <HeartPlus size={20} />
        </span>
        <span
          className="journey-action journey-action--muted"
          title="Comments — Coming Soon. Stay tuned!"
          aria-label="Comments coming soon"
        >
          <MessageCircleReply size={20} />
        </span>

        {me && (
          <button
            type="button"
            className="journey-action journey-action--btn"
            aria-label="Share to a friend"
            title="Share to a friend"
            onClick={() => setShareOpen(true)}
          >
            <MessageSquareShare size={20} />
          </button>
        )}

        <button
          type="button"
          className="journey-action journey-action--btn"
          aria-label="Share"
          title="Share"
          onClick={handleExternalShare}
        >
          <SquareArrowOutUpRight size={20} />
        </button>
      </div>

      {/* Share-to-friend modal (uses canonical + #journeys) */}
      <ShareToFriendModalRedux
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={buildShareUrl()}
        title="Journeys"
        preview={{
          poster: undefined,
          ownerHandle: undefined,
        }}
      />
    </section>
  );
}
